// screens/Admin/Invoices/RecordTable/RefundModal.jsx
import React, { useMemo, useState, useEffect } from 'react';

function money(n, c = 'USD') {
  const v = Number(n || 0);
  try { return v.toLocaleString(undefined, { style: 'currency', currency: (c || 'USD').toUpperCase() }); }
  catch { return `$${v.toFixed(2)}`; }
}
const trimZeros = (n) => {
  const s = String(n);
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
};

export default function RefundModal({ open, invoice, onClose, onSubmit, submitting = false }) {
  const currency = (invoice?.currency || 'USD').toUpperCase();

  // Aggregate fuel lines and compute caps
  const { totalGallons, totalFuelAmount, avgPrice, maxRefundableMoney } = useMemo(() => {
    const fuelLines = (invoice?.lines || []).filter(
      (li) => li?.fuelTypeId || String(li?.description || '').toLowerCase().startsWith('fuel')
    );
    const tGallons = fuelLines.reduce((s, li) => s + Number(li.quantity || 0), 0);
    const tAmount  = fuelLines.reduce((s, li) => s + Number(li.amount || 0), 0);
    const aPrice   = tGallons > 0 ? (tAmount / tGallons) : 0;

    const refundedTotal = Number(invoice?.refundedTotal || 0);
    const maxMoney = Math.max(0, Number(invoice?.total || 0) - refundedTotal);

    return { totalGallons: tGallons, totalFuelAmount: tAmount, avgPrice: aPrice, maxRefundableMoney: maxMoney };
  }, [invoice]);

  const maxRefundableGallons = useMemo(() => {
    if (avgPrice <= 0) return 0;
    const byMoney = maxRefundableMoney / avgPrice;
    return Math.max(0, Math.min(totalGallons, byMoney));
  }, [totalGallons, avgPrice, maxRefundableMoney]);

  // UI state
  const [full, setFull] = useState(true);

  // Keep a text state to allow natural typing like ".", "0.", "10.", "10.5"
  const [gallonsText, setGallonsText] = useState(trimZeros((maxRefundableGallons || 0).toFixed(2)));
  const [includeDelivery, setIncludeDelivery] = useState(!!invoice?.deliveryFee);
  const [includeEmergency, setIncludeEmergency] = useState(!!invoice?.emergencyFee);

  useEffect(() => {
    setFull(true);
    setGallonsText(trimZeros((maxRefundableGallons || 0).toFixed(2)));
    setIncludeDelivery(!!invoice?.deliveryFee);
    setIncludeEmergency(!!invoice?.emergencyFee);
  }, [invoice, maxRefundableGallons]);

  // Validate typing: allow "", ".", "0.", "10.", "10.5", up to 2 decimals
  const DECIMAL_PATTERN = /^\d*(?:\.\d{0,2})?$/;

  const onGallonsChange = (e) => {
    if (submitting) return;
    let t = String(e.target.value ?? '');

    // Normalize commas to dots if user uses comma
    t = t.replace(',', '.');

    // Remove non-numeric (except dot)
    t = t.replace(/[^0-9.]/g, '');

    // Disallow multiple dots
    const parts = t.split('.');
    if (parts.length > 2) {
      t = parts[0] + '.' + parts.slice(1).join('');
    }

    // Only update if pattern matches (prevents jumpy cursor)
    if (t === '' || t === '.' || DECIMAL_PATTERN.test(t)) {
      setGallonsText(t);
    }
  };

  // On blur, clamp and normalize display
  const onGallonsBlur = () => {
    let num = parseFloat(gallonsText);
    if (!isFinite(num)) num = 0;
    if (num > maxRefundableGallons) num = maxRefundableGallons;
    // keep up to 2 decimals but drop trailing zeros (so 10.50 -> 10.5, 10.00 -> 10)
    setGallonsText(trimZeros(num.toFixed(2)));
  };

  // Numeric value used for calculations (clamped)
  const gallonsNumRaw = parseFloat(gallonsText);
  const gallonsNum = Math.min(
    Math.max(0, isFinite(gallonsNumRaw) ? gallonsNumRaw : 0),
    maxRefundableGallons
  );

  const baseFuel = full
    ? Math.min(totalGallons, maxRefundableGallons) * avgPrice
    : gallonsNum * avgPrice;

  const fees =
    (full && includeDelivery ? Number(invoice?.deliveryFee || 0) : 0) +
    (full && includeEmergency ? Number(invoice?.emergencyFee || 0) : 0);

  let proposed = Number((baseFuel + fees).toFixed(2));
  proposed = Math.min(proposed, maxRefundableMoney);

  const nothingLeft = proposed <= 0 || (!full && maxRefundableGallons <= 0);

  if (!open) return null;

  return (
    <div style={styles.backdrop} onClick={() => !submitting && onClose?.()}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Refund</h3>

        <div style={styles.row}><span style={styles.label}>Invoice</span><span style={styles.value}>{invoice?.invoiceNumber || '—'}</span></div>
        <div style={styles.row}><span style={styles.label}>Paid Total</span><span style={styles.value}>{money(invoice?.total || 0, currency)}</span></div>
        <div style={styles.row}><span style={styles.label}>Already Refunded</span><span style={styles.value}>{money(invoice?.refundedTotal || 0, currency)}</span></div>
        <div style={styles.row}><span style={styles.label}>Max Refundable</span><span style={styles.value}>{money(maxRefundableMoney, currency)}</span></div>

        <div style={styles.sep} />

        <label style={styles.switchRow}>
          <input type="checkbox" checked={full} onChange={() => !submitting && setFull(v => !v)} />
          <span style={styles.switchLabel}>Refund full amount of fuel</span>
        </label>

        {!full && (
          <>
            <div style={styles.row}><span style={styles.label}>Total Delivered Gallons</span><span style={styles.value}>{totalGallons.toFixed(2)}</span></div>
            <div style={styles.row}><span style={styles.label}>Avg Price / Gallon</span><span style={styles.value}>{money(avgPrice, currency)}</span></div>

            <div style={{ marginTop: 8 }}>
              <div style={styles.inputLabel}>Gallons to refund</div>
              <input
                value={gallonsText}
                onChange={onGallonsChange}
                onBlur={onGallonsBlur}
                placeholder="0"
                inputMode="decimal"
                style={styles.input}
                disabled={submitting}
              />
              <div style={styles.hint}>
                Available to refund: <strong>{trimZeros(maxRefundableGallons.toFixed(2))}</strong> gal
              </div>
            </div>
          </>
        )}

        {full && (
          <>
            {!!invoice?.deliveryFee && (
              <label style={styles.switchRow}>
                <input type="checkbox" checked={includeDelivery} disabled={submitting} onChange={() => setIncludeDelivery(v => !v)} />
                <span style={styles.switchLabel}>Include delivery fee ({money(invoice.deliveryFee, currency)})</span>
              </label>
            )}
            {!!invoice?.emergencyFee && (
              <label style={styles.switchRow}>
                <input type="checkbox" checked={includeEmergency} disabled={submitting} onChange={() => setIncludeEmergency(v => !v)} />
                <span style={styles.switchLabel}>Include emergency fee ({money(invoice.emergencyFee, currency)})</span>
              </label>
            )}
            <div style={{ ...styles.hint, marginTop: 6 }}>
              Fuel gallons refundable now: <strong>{trimZeros(Math.min(totalGallons, maxRefundableGallons).toFixed(2))}</strong> gal
            </div>
          </>
        )}

        <div style={styles.sep} />

        <div style={styles.row}><span style={{ ...styles.label, fontWeight: 700 }}>Refund Amount</span><span style={{ ...styles.value, fontWeight: 700 }}>{money(proposed, currency)}</span></div>

        <div style={styles.actions}>
          <button style={styles.btn} disabled={submitting} onClick={() => !submitting && onClose?.()}>Cancel</button>
          <button
            style={{ ...styles.btn, ...styles.primary, ...(nothingLeft || submitting ? styles.btnDisabled : {}) }}
            disabled={nothingLeft || submitting}
            onClick={() => onSubmit?.({
              full,
              gallons: full ? Math.min(totalGallons, maxRefundableGallons) : gallonsNum,
              includeDelivery: !!includeDelivery,
              includeEmergency: !!includeEmergency,
            })}
          >
            {submitting ? 'Refunding…' : 'Refund'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  },
  panel: {
    width: 'min(520px, 92vw)', background: '#fff', borderRadius: 12, padding: 16,
    boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
  },
  row: { display: 'flex', justifyContent: 'space-between', marginTop: 6 },
  label: { fontSize: 12, color: '#666' },
  value: { fontSize: 12, color: '#111', fontWeight: 600 },
  sep: { height: 1, background: '#eee', margin: '12px 0' },
  switchRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer' },
  switchLabel: { fontSize: 12, color: '#111' },
  inputLabel: { fontSize: 12, color: '#444', marginBottom: 6 },
  input: {
    width: '100%', border: '1px solid #e3e6ea', borderRadius: 8, padding: '8px 10px',
    fontSize: 14, color: '#111'
  },
  hint: { marginTop: 4, color: '#888', fontSize: 11 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
  btn: { padding: '10px 14px', borderRadius: 8, background: '#f3f4f6', border: 'none', cursor: 'pointer' },
  primary: { background: '#111', color: '#fff' },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};
